import { shell } from '../app';
import { currentPosition, locateLeads } from '../data/geocode';
import { getSnapshot } from '../data/store';
import { todayLocal } from '../logic/dates';
import { followUpTargets, planFollowUpRoute } from '../logic/followupRoute';
import { btn } from '../ui/components';
import { currentPath } from '../ui/router';
import { commitPlannedRoute, planErrorMessage, routeAnchor } from './routeCommit';

/** One plan at a time, across re-renders: Brief rebuilds this button whenever the mirror
 *  changes (a sync can land mid-plan), and a fresh enabled copy must not start a second run. */
let planning = false;
const live = new Set<HTMLButtonElement>();

const paint = (b: HTMLButtonElement) => {
  b.disabled = planning;
  b.textContent = planning ? 'Planning…' : (b.dataset.label ?? '');
};

const setPlanning = (on: boolean) => {
  planning = on;
  for (const b of live) {
    if (b.isConnected || on) paint(b);
    else live.delete(b);
  }
};

/** Route all due follow-ups: one tap from Brief to a walking route on the Route tab,
 *  nearest-first from where the phone is (or from the first follow-up when location is off). */
export const followUpRouteButton = (count: number): HTMLButtonElement => {
  const label = `Route all ${count} due follow-up${count === 1 ? '' : 's'}`;

  const plan = async () => {
    if (planning) return;
    const origin = currentPath();
    setPlanning(true);
    try {
      const snap = await getSnapshot();
      const targets = followUpTargets(snap.leads, snap.visits, todayLocal());
      const [here, located] = await Promise.all([currentPosition(), locateLeads(targets.map((t) => t.lead))]);
      const first = targets.find((t) => located.has(t.lead.id));
      const firstAt = first && located.get(first.lead.id);
      if (!first || !firstAt) {
        shell.toast("None of today's follow-ups has a confirmed location — repair the location on the lead.", {
          kind: 'error',
        });
        return;
      }
      const anchor = routeAnchor(here, { place_id: `lead:${first.lead.id}` });
      const { route, unlocated } = planFollowUpRoute(targets, located, anchor, new Date().toISOString());
      commitPlannedRoute(route, unlocated.length, origin);
    } catch (err) {
      shell.toast(planErrorMessage(err), { kind: 'error' });
    } finally {
      setPlanning(false);
    }
  };

  const button = btn(label, { variant: 'ghost', class: 'w-full', onClick: () => void plan() });
  button.dataset.label = label;
  live.add(button);
  paint(button);
  // buttons of renders since replaced are released now, not only when the next plan runs
  queueMicrotask(() => {
    for (const b of live) if (!b.isConnected) live.delete(b);
  });
  return button;
};
